import React, { FC, ReactNode, useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Save, State, StorageContext } from './StorageContext'

type StorageProviderProps = {
  flowId: string
  children?: ReactNode
}

const SEARCH_PARAMS_OVERRIDABLE_FIELDS: (keyof State)[] = ['businessName', 'logo']

export const StorageProvider: FC<StorageProviderProps> = ({ flowId, children }) => {
  const [searchParams] = useSearchParams()
  const initialState: Partial<State> = SEARCH_PARAMS_OVERRIDABLE_FIELDS.reduce(
    (acc, key) => ({ ...acc, [key]: searchParams.get(key) || undefined }),
    {},
  )
  // Restored synchronously too: consumers that act on mount must not see an empty first render
  const [state, setState] = useState<State>(() => {
    const rawData = sessionStorage.getItem(flowId)
    return rawData ? JSON.parse(rawData) : initialState
  })

  useEffect(() => {
    const rawData = sessionStorage.getItem(flowId)
    if (!rawData) {
      return
    }

    setState(JSON.parse(rawData))
  }, [flowId])

  useEffect(() => {
    sessionStorage.setItem(flowId, JSON.stringify(state))
  }, [flowId, state])

  const save = useCallback<Save>(update => setState(prev => ({ ...prev, ...update })), [])

  return <StorageContext.Provider value={{ ...state, save }}>{children}</StorageContext.Provider>
}
